class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  applySectionHTML(sectionElement, sectionHTML, options = {}) {
    if (sectionElement.innerHTML.trim() === sectionHTML.trim()) return false;

    const preserveCartImages = options.preserveCartImages === true;
    const oldItems = sectionElement.querySelectorAll('tr.cart-item, .cart-item');

    if (!preserveCartImages || !oldItems.length) {
      sectionElement.innerHTML = sectionHTML;
      return true;
    }

    const parsed = new DOMParser().parseFromString(
      `<div id="arch-cart-section-root">${sectionHTML}</div>`,
      'text/html'
    );
    const newRoot = parsed.getElementById('arch-cart-section-root');
    const newItems = newRoot ? newRoot.querySelectorAll('tr.cart-item, .cart-item') : [];

    if (!newItems.length || newItems.length !== oldItems.length) {
      sectionElement.innerHTML = sectionHTML;
      return true;
    }

    newItems.forEach((newItem, index) => {
      const oldItem = oldItems[index];
      if (!oldItem) return;

      const newImg = newItem.querySelector('img.cart-item__image');
      const oldImg = oldItem.querySelector('img.cart-item__image');
      const newSrc = newImg && newImg.getAttribute('src');
      const oldSrc = oldImg && oldImg.getAttribute('src');
      const replacement = newItem.cloneNode(true);

      if (newImg && oldImg && newSrc && oldSrc && newSrc === oldSrc) {
        const cloneImg = replacement.querySelector('img.cart-item__image');
        if (cloneImg) cloneImg.replaceWith(oldImg);
      }

      oldItem.replaceWith(replacement);
    });

    const footerSelectors = ['.drawer__footer', '.cart-drawer__footer', '.cart__ctas', '.totals'];
    footerSelectors.forEach((selector) => {
      const newEl = newRoot && newRoot.querySelector(selector);
      const oldEl = sectionElement.querySelector(selector);
      if (newEl && oldEl) oldEl.replaceWith(newEl.cloneNode(true));
    });

    return true;
  }

  renderContents(parsedState = {}) {
    this.classList.contains('is-empty') && this.classList.remove('is-empty');

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id || this.productId;

    const sections = parsedState.sections || {};
    const shouldOpenDrawer = parsedState.openDrawer !== false;
    const drawerAlreadyOpen = this.classList.contains('active');
    const preserveCartImages = parsedState.preserveCartImages === true;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
      if (sectionHTML === null) return;

      if (section.id === 'cart-drawer') {
        this.applySectionHTML(sectionElement, sectionHTML, { preserveCartImages });
        return;
      }

      if (sectionElement.innerHTML.trim() === sectionHTML.trim()) return;

      sectionElement.innerHTML = sectionHTML;
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));

      if (shouldOpenDrawer && !drawerAlreadyOpen) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || !html.trim()) return null;

    const sectionElement = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return sectionElement ? sectionElement.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
